<template>
  <ListPageShell>
    <template #header>
    <!-- 页面标题 -->
    <div class="list-page-heading mb-6">
      <div class="list-page-heading-main">
        <h1 class="list-page-title">提现审核</h1>
        <p class="list-page-description">管理用户提现申请，进行审核操作</p>
      </div>
    </div>
    </template>

    <!-- 表格区域 -->
    <div class="list-page-table-panel">
      <PageFilterBar
        shell-class="filter-bar-shell is-embedded"
        body-class="filter-bar-body is-panel"
        :show-refresh="true"
        :refresh-loading="loading"
        @refresh="handleSearch"
      >
        <div class="filter-bar-field is-wide relative">
          <input
            v-model="filters.keyword"
            type="text"
            placeholder="用户ID / 姓名 / 银行"
            class="form-search-input form-control-sm w-full"
            @keyup.enter="handleSearch"
          >
          <svg class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
        </div>
        <el-select
          v-model="filters.status"
          placeholder="请选择状态"
          class="form-select form-control-sm filter-bar-field"
        >
          <el-option label="全部状态" value="" />
          <el-option label="待审核" value="pending" />
          <el-option label="已通过" value="approved" />
          <el-option label="已拒绝" value="rejected" />
        </el-select>
        <el-date-picker
          v-model="filters.dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          class="filter-bar-field is-wide form-date form-control-sm"
          value-format="YYYY-MM-DD"
        />
        <input
          v-model="filters.amountMin"
          type="number"
          placeholder="最低金额"
          class="form-input form-control-sm filter-bar-field"
        >
        <input
          v-model="filters.amountMax"
          type="number"
          placeholder="最高金额"
          class="form-input form-control-sm filter-bar-field"
        >
        <template #searchActions>
          <button type="button" class="btn-primary" @click="handleSearch">搜索</button>
          <button type="button" class="btn-secondary" @click="handleReset">重置</button>
        </template>
      </PageFilterBar>
      <div class="data-table-shell">
        <div class="data-table-scroll">
          <table class="data-table text-sm">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th text-left w-10">
                  <input type="checkbox" @change="toggleAllRows" :checked="isAllSelected" class="form-check">
                </th>
                <th class="data-table-th text-left">ID</th>
                <th class="data-table-th text-left">用户信息</th>
                <th class="data-table-th text-right">提现金额</th>
                <th class="data-table-th text-left">银行</th>
                <th class="data-table-th text-left">状态</th>
                <th class="data-table-th text-left">申请时间</th>
                <th class="data-table-th text-center w-36">操作</th>
              </tr>
            </thead>
            <tbody>
              <!-- 骨架屏 -->
              <template v-if="loading">
                <tr v-for="n in 5" :key="n" class="animate-pulse">
                  <td class="data-table-td is-center"><div class="h-4 w-4 bg-slate-200 rounded"/></td>
                  <td class="data-table-td"><div class="h-4 w-24 bg-slate-200 rounded"/></td>
                  <td class="data-table-td"><div class="flex items-center gap-3"><div class="h-8 w-8 bg-slate-200 rounded-full"/><div class="h-4 w-20 bg-slate-200 rounded"/></div></td>
                  <td class="data-table-td is-right"><div class="h-4 w-16 bg-slate-200 rounded ml-auto"/></td>
                  <td class="data-table-td"><div class="h-4 w-32 bg-slate-200 rounded"/></td>
                  <td class="data-table-td is-center"><div class="h-6 w-16 bg-slate-200 rounded-full"/></td>
                  <td class="data-table-td"><div class="h-4 w-24 bg-slate-200 rounded"/></td>
                  <td class="data-table-td is-center"><div class="h-8 w-20 bg-slate-200 rounded"/></td>
                </tr>
              </template>
              <!-- 空状态 -->
              <tr v-else-if="tableData.length === 0">
                <td colspan="8" class="data-table-empty">
                  <div class="flex flex-col items-center">
                    <svg class="w-12 h-12 text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                    <span>暂无提现记录</span>
                  </div>
                </td>
              </tr>
              <tr v-for="row in tableData" :key="row.id" class="data-table-row">
                <td class="data-table-td is-center">
                  <input type="checkbox" :checked="selectedIds.includes(row.id)" @change="toggleRow(row.id)" class="form-check">
                </td>
                <td class="data-table-td font-mono text-slate-900">{{ row.id }}</td>
                <td class="data-table-td">
                  <div class="flex items-center gap-2">
                    <div class="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-600">
                      {{ row.userName?.charAt(0) || 'U' }}
                    </div>
                    <div>
                      <div class="text-sm text-slate-900">{{ row.userName }}</div>
                      <div class="text-xs text-slate-500">ID: {{ row.userId }}</div>
                    </div>
                  </div>
                </td>
                <td class="data-table-td is-right money-text">￥{{ row.amount?.toLocaleString() }}</td>
                <td class="data-table-td">
                  <div class="text-sm text-slate-900">{{ row.bankName }}</div>
                  <div class="text-xs text-slate-500">{{ row.bankCardNo?.replace(/(\d{4})\d+(\d{4})/, '$1****$2') }}</div>
                </td>
                <td class="data-table-td">
                  <span class="status-badge" :class="statusMap[row.status]?.class">
                    <span class="w-1.5 h-1.5 rounded-full mr-1" :class="statusMap[row.status]?.dot"/>
                    {{ statusMap[row.status]?.label }}
                  </span>
                </td>
                <td class="data-table-td text-slate-600">{{ row.applyTime }}</td>
                <td class="data-table-td is-center">
                  <div class="flex items-center justify-center gap-1">
                    <button v-if="row.status === 'pending'" v-permission="'commission:withdraw:audit'" class="table-action-button is-success" title="通过" aria-label="通过" @click="handleApprove(row)">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                    </button>
                    <button v-if="row.status === 'pending'" v-permission="'commission:withdraw:audit'" class="table-action-button is-danger" title="拒绝" aria-label="拒绝" @click="handleReject(row)">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                    <button class="table-action-button is-primary" title="查看详情" aria-label="查看详情" @click="handleViewDetail(row)">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 分页 -->
      <AppPagination
        class="pagination-bridge"
        :pagination="pagination"
        :total="total"
        :page-sizes="[10, 20, 50]"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
    </div>

    <Teleport to="body">
      <div v-if="detailDialogVisible" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="fixed inset-0 bg-black/50" @click="detailDialogVisible = false"/>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <!-- 标题栏 -->
            <div class="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 class="text-lg font-semibold text-slate-900">提现详情</h3>
                <p class="text-sm text-slate-500 font-mono">{{ currentRow?.id }}</p>
              </div>
              <div class="flex items-center gap-3">
                <span v-if="currentRow" class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium" :class="statusMap[currentRow.status]?.class">
                  <span class="w-2 h-2 rounded-full mr-1.5" :class="statusMap[currentRow.status]?.dot"/>
                  {{ statusMap[currentRow.status]?.label }}
                </span>
                <button class="p-1.5 text-slate-400 hover:text-slate-600" @click="detailDialogVisible = false">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- 内容区域 -->
            <div class="flex-1 overflow-y-auto min-h-0 p-6 space-y-4">
              <!-- 用户信息 -->
              <div class="bg-slate-50 rounded-xl p-4">
                <h4 class="text-sm font-medium text-slate-700 mb-3 flex items-center">
                  <svg class="w-4 h-4 mr-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                  </svg>
                  用户信息
                </h4>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <div class="text-xs text-slate-500 mb-1">用户ID</div>
                    <div class="text-sm text-slate-900 font-mono">{{ currentRow?.userId || '-' }}</div>
                  </div>
                  <div>
                    <div class="text-xs text-slate-500 mb-1">用户姓名</div>
                    <div class="text-sm text-slate-900">{{ currentRow?.userName || '-' }}</div>
                  </div>
                </div>
              </div>

              <!-- 提现信息 -->
              <div class="bg-slate-50 rounded-xl p-4">
                <h4 class="text-sm font-medium text-slate-700 mb-3 flex items-center">
                  <svg class="w-4 h-4 mr-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  提现信息
                </h4>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div class="text-xs text-slate-500 mb-1">提现金额</div>
                    <div class="text-xl money-text">￥{{ currentRow?.amount?.toLocaleString() || '0' }}</div>
                  </div>
                  <div>
                    <div class="text-xs text-slate-500 mb-1">手续费</div>
                    <div class="text-sm text-slate-900">￥{{ currentRow?.fee?.toLocaleString() || '0.00' }}</div>
                  </div>
                  <div>
                    <div class="text-xs text-slate-500 mb-1">实际到账</div>
                    <div class="text-sm text-emerald-600 font-medium">￥{{ ((currentRow?.amount || 0) - (currentRow?.fee || 0)).toLocaleString() }}</div>
                  </div>
                  <div>
                    <div class="text-xs text-slate-500 mb-1">申请时间</div>
                    <div class="text-sm text-slate-900">{{ currentRow?.applyTime || '-' }}</div>
                  </div>
                </div>
              </div>

              <!-- 银行卡信息 -->
              <div class="bg-slate-50 rounded-xl p-4">
                <h4 class="text-sm font-medium text-slate-700 mb-3 flex items-center">
                  <svg class="w-4 h-4 mr-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                  </svg>
                  银行卡信息
                </h4>
                <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <div class="text-xs text-slate-500 mb-1">开户银行</div>
                    <div class="text-sm text-slate-900">{{ currentRow?.bankName || '-' }}</div>
                  </div>
                  <div>
                    <div class="text-xs text-slate-500 mb-1">卡号</div>
                    <div class="text-sm text-slate-900 font-mono">{{ currentRow?.bankCardNo || '-' }}</div>
                  </div>
                  <div>
                    <div class="text-xs text-slate-500 mb-1">开户姓名</div>
                    <div class="text-sm text-slate-900">{{ currentRow?.bankAccountName || '-' }}</div>
                  </div>
                </div>
              </div>

              <!-- 审核信息 -->
              <div v-if="currentRow?.status !== 'pending'" class="bg-slate-50 rounded-xl p-4">
                <h4 class="text-sm font-medium text-slate-700 mb-3 flex items-center">
                  <svg class="w-4 h-4 mr-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  审核信息
                </h4>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <div class="text-xs text-slate-500 mb-1">审核时间</div>
                    <div class="text-sm text-slate-900">{{ currentRow?.auditTime || '-' }}</div>
                  </div>
                  <div v-if="currentRow?.auditRemark">
                    <div class="text-xs text-slate-500 mb-1">审核备注</div>
                    <div class="text-sm text-slate-900">{{ currentRow.auditRemark }}</div>
                  </div>
                </div>
              </div>

              <!-- 拒绝原因 -->
              <div v-if="showRejectInput" class="bg-red-50 rounded-xl p-4">
                <h4 class="text-sm font-medium text-red-700 mb-3">拒绝原因</h4>
                <textarea
                  v-model="rejectReason"
                  rows="3"
                  placeholder="请输入拒绝原因（选填）"
                  class="w-full px-3 py-2 border border-red-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                ></textarea>
              </div>
            </div>

            <!-- 底部操作 -->
            <div class="flex-shrink-0 px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button class="btn-secondary" @click="detailDialogVisible = false">关闭</button>
              <template v-if="currentRow?.status === 'pending'">
                <button v-permission="'commission:withdraw:audit'" class="btn-danger-secondary" @click="showRejectInput = true; showRejectInput && confirmReject()">拒绝</button>
                <button v-permission="'commission:withdraw:audit'" class="btn-primary" @click="handleApproveFromDialog">通过</button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </ListPageShell>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'

// Status mapping (light mode colors)
const statusMap = {
  pending: { label: '待审核', class: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  approved: { label: '已通过', class: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  rejected: { label: '已拒绝', class: 'bg-red-100 text-red-700', dot: 'bg-red-500' }
}

// Filters
const filters = reactive({
  status: '',
  dateRange: null,
  amountMin: '',
  amountMax: '',
  keyword: ''
})

// Table data (mock)
const tableData = ref([
  {
    id: 'WX20260403001',
    userId: '10001',
    userName: '张明',
    amount: 15800,
    fee: 15.80,
    bankName: '中国工商银行',
    bankCardNo: '6222021234567890123',
    bankAccountName: '张明',
    status: 'pending',
    applyTime: '2026-04-03 14:32:15'
  },
  {
    id: 'WX20260403002',
    userId: '10002',
    userName: '李华',
    amount: 5600,
    fee: 5.60,
    bankName: '中国建设银行',
    bankCardNo: '6217001234567890123',
    bankAccountName: '李华',
    status: 'approved',
    applyTime: '2026-04-03 11:20:00',
    auditTime: '2026-04-03 11:35:22'
  },
  {
    id: 'WX20260403003',
    userId: '10003',
    userName: '王芳',
    amount: 28000,
    fee: 28.00,
    bankName: '中国农业银行',
    bankCardNo: '6228481234567890123',
    bankAccountName: '王芳',
    status: 'pending',
    applyTime: '2026-04-03 10:15:33'
  },
  {
    id: 'WX20260402015',
    userId: '10004',
    userName: '刘洋',
    amount: 4200,
    fee: 4.20,
    bankName: '招商银行',
    bankCardNo: '6214851234567890',
    bankAccountName: '刘洋',
    status: 'rejected',
    applyTime: '2026-04-02 16:45:00',
    auditTime: '2026-04-02 17:10:05',
    auditRemark: '银行卡信息错误'
  },
  {
    id: 'WX20260402014',
    userId: '10005',
    userName: '陈润',
    amount: 9850,
    fee: 9.85,
    bankName: '交通银行',
    bankCardNo: '6222601234567890123',
    bankAccountName: '陈润',
    status: 'approved',
    applyTime: '2026-04-02 09:30:00',
    auditTime: '2026-04-02 09:45:18'
  }
])

const loading = ref(false)
const total = ref(5)
const pagination = reactive({
  current: 1,
  size: 10,
  pages: 1
})
const selectedIds = ref([])

const isAllSelected = computed(() => {
  return tableData.value.length > 0 && tableData.value.every(row => selectedIds.value.includes(row.id))
})

// Dialog states
const detailDialogVisible = ref(false)
const currentRow = ref(null)
const rejectReason = ref('')
const showRejectInput = ref(false)

// Handlers
function handleSearch() {
  loading.value = true
  setTimeout(() => {
    loading.value = false
    ElMessage.success('搜索完成')
  }, 500)
}

function handleReset() {
  filters.status = ''
  filters.dateRange = null
  filters.amountMin = ''
  filters.amountMax = ''
  filters.keyword = ''
  handleSearch()
}

function handlePageChange(page) {
  if (page < 1 || page > pagination.pages) return
  pagination.current = page
  handleSearch()
}

function handlePageSizeChange(size) {
  pagination.size = size
  pagination.pages = Math.ceil(total.value / pagination.size)
  pagination.current = 1
  handleSearch()
}

function toggleRow(id) {
  const idx = selectedIds.value.indexOf(id)
  if (idx > -1) {
    selectedIds.value.splice(idx, 1)
  } else {
    selectedIds.value.push(id)
  }
}

function toggleAllRows(e) {
  if (e.target.checked) {
    selectedIds.value = tableData.value.map(row => row.id)
  } else {
    selectedIds.value = []
  }
}

function handleViewDetail(row) {
  currentRow.value = { ...row }
  rejectReason.value = ''
  showRejectInput.value = false
  detailDialogVisible.value = true
}

function handleApprove(row) {
  ElMessageBox.confirm(`确认通过用户"${row.userName}"的提现申请吗？`, '审核确认', {
    confirmButtonText: '确认通过',
    cancelButtonText: '取消',
    type: 'success'
  }).then(() => {
    row.status = 'approved'
    row.auditTime = new Date().toLocaleString()
    ElMessage.success('已通过')
  }).catch(() => {})
}

function handleApproveFromDialog() {
  if (!currentRow.value) return
  ElMessageBox.confirm(`确认通过用户"${currentRow.value.userName}"的提现申请吗？`, '审核确认', {
    confirmButtonText: '确认通过',
    cancelButtonText: '取消',
    type: 'success'
  }).then(() => {
    currentRow.value.status = 'approved'
    currentRow.value.auditTime = new Date().toLocaleString()
    // sync back to table
    const row = tableData.value.find(r => r.id === currentRow.value.id)
    if (row) Object.assign(row, currentRow.value)
    detailDialogVisible.value = false
    ElMessage.success('已通过')
  }).catch(() => {})
}

function handleReject(row) {
  ElMessageBox.confirm('确认拒绝此提现申请吗？', '审核确认', {
    confirmButtonText: '确认拒绝',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    row.status = 'rejected'
    row.auditTime = new Date().toLocaleString()
    row.auditRemark = ''
    ElMessage.success('已拒绝')
  }).catch(() => {})
}

function confirmReject() {
  if (!showRejectInput.value) return
  if (!currentRow.value) return
  ElMessageBox.confirm(
    rejectReason.value ? `拒绝原因：${rejectReason.value}` : '确认拒绝此提现申请吗？',
    '审核确认',
    { confirmButtonText: '确认', cancelButtonText: '取消', type: 'warning' }
  ).then(() => {
    currentRow.value.status = 'rejected'
    currentRow.value.auditTime = new Date().toLocaleString()
    currentRow.value.auditRemark = rejectReason.value
    const row = tableData.value.find(r => r.id === currentRow.value.id)
    if (row) Object.assign(row, currentRow.value)
    detailDialogVisible.value = false
    ElMessage.success('已拒绝')
  }).catch(() => {})
}

onMounted(() => {
  pagination.pages = Math.ceil(total.value / pagination.size)
})
</script>
